import { useRouter } from 'next/router'
import React from 'react'

import {
  useMutationDisableBookingInsurance,
  useMutationEnableBookingInsurance,
  useQueryGetAllBookingInsurances,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { type UNSAFE_any } from '@flecto/types'
import { type EInsuranceMetaStatus } from '@flecto/types/src/enums/EInsuranceMetaStatus'
import { EInsuranceStatus } from '@flecto/types/src/enums/EInsuranceStatus'

import { useInsuranceStatus } from '../..'
import useRefreshInsurancePending from '../components/InsuranceCard/hooks/useRefreshInsurancePending'
import { type IInsuranceManagerContext } from './types'

export const InsuranceManagerContext =
  React.createContext<IInsuranceManagerContext>({} as IInsuranceManagerContext)

export default function InsuranceProvider({
  children,
}: React.PropsWithChildren) {
  const router = useRouter()
  const id = Number(router.query.id)

  const {
    data,
    isLoading,
    refetch: refetchInsurances,
    remove: resetInsurance,
  } = useQueryGetAllBookingInsurances({ bookingId: id })

  const {
    data: booking,
    isLoading: isBookingLoading,
    refetch,
    isRefetching: isBookingRefetching,
    remove: removeBookingCache,
  } = useQueryGetBookingById({ bookingId: id })

  const { data: insuranceStatus, isLoading: isLoadingInsuranceStatus } =
    useInsuranceStatus({ bookingId: id })

  const { mutateAsync: enableInsurance, isLoading: isEnabling } =
    useMutationEnableBookingInsurance()
  const { mutate: disableInsurance, isLoading: isDisabling } =
    useMutationDisableBookingInsurance()

  const toggleInsured = () => {
    if (isEnabling || isDisabling) return
    if (booking?.insured) return disableInsurance({ bookingId: id })
    enableInsurance({ bookingId: id })
  }

  React.useEffect(() => {
    if (insuranceStatus === 'pending' && !isLoadingInsuranceStatus) {
      useRefreshInsurancePending({
        onUpdate: () => {
          refetchInsurances()
        },
      })
    }
  }, [insuranceStatus, isLoadingInsuranceStatus, refetchInsurances])

  const isClaimPeriod = React.useMemo(() => {
    const rows = data?.insurance ?? []
    if (!rows.length) return false
    if (
      rows.some((item: UNSAFE_any) => item.status === EInsuranceStatus.ACTIVE)
    ) {
      return false
    }

    const sortedClaim = rows
      .filter((item: UNSAFE_any) => item.status === EInsuranceStatus.FINISHED)
      .sort((a: UNSAFE_any, b: UNSAFE_any) => {
        return (
          new Date(b.claim_until).getTime() - new Date(a.claim_until).getTime()
        )
      })
    const longestClaimPeriod = sortedClaim.at(0)
    const now = new Date()

    if (longestClaimPeriod && now > new Date(longestClaimPeriod.claim_until)) {
      return false
    }
    return true
  }, [data])

  const dispose = () => {
    removeBookingCache()
    resetInsurance()
  }

  React.useEffect(() => {
    return dispose
  }, [])

  const countInsurances = data?.insurance?.length ?? 0
  return (
    <InsuranceManagerContext.Provider
      value={{
        insurances: {
          data: data?.insurance ?? [],
          loading: isLoading ?? false,
          meta: {
            count: data?.total_count ?? 0,
            metaStatus: data?.meta_status as EInsuranceMetaStatus,
            totalCents: data?.total_cents ?? 0,
            until: data?.until as string,
          },
          isClaimPeriod,
        },
        toggleInsurance: {
          enable: () => enableInsurance({ bookingId: id }),
          disable: () => disableInsurance({ bookingId: id }),
          loading: isEnabling || isDisabling,
          toggle: toggleInsured,
        },
        booking: {
          data: booking,
          loading: isBookingLoading || isBookingRefetching,
          refresh: () => {
            refetch()
          },
        },
        hasInsurance: countInsurances > 0,
        countInsurances,
      }}
    >
      {children}
    </InsuranceManagerContext.Provider>
  )
}

export const useInsuranceManagerContext = () => {
  const context = React.useContext(InsuranceManagerContext)

  if (!context) {
    throw new Error(
      'useInsuranceManagerContext must be used within a InsuranceManagerContext'
    )
  }

  return context
}
